/**
 * Step 3: CTA adaptatif. Fetch /api/ux-cta avec timeout 150ms, cache 60s par session.
 * Fallback instantané si flags OFF, rollout 0, timeout ou erreur.
 */

import { useState, useEffect, useRef } from "react";
import { getUxFlags } from "@/lib/uxFlags";
import { UX_SESSION_STORAGE_KEY } from "@/lib/uxTracking";

const FETCH_TIMEOUT_MS = 150;
const CACHE_TTL_MS = 60_000;

export type CtaVariant = "soft_support" | "protocol" | "book_now";

export interface AdaptiveCtaResult {
  primaryLabel: string;
  secondaryLabel: string | undefined;
  variant: CtaVariant | undefined;
  tone: "soft" | "neutral" | "strong";
  isAdaptive: boolean;
}

interface CtaCacheEntry {
  data: AdaptiveCtaResult;
  expiresAt: number;
}

let memoryCache: { sessionId: string; entry: CtaCacheEntry } | null = null;

function getCached(sessionId: string): AdaptiveCtaResult | null {
  if (!memoryCache || memoryCache.sessionId !== sessionId || Date.now() >= memoryCache.entry.expiresAt) {
    return null;
  }
  return memoryCache.entry.data;
}

function setCached(sessionId: string, data: AdaptiveCtaResult): void {
  memoryCache = {
    sessionId,
    entry: { data, expiresAt: Date.now() + CACHE_TTL_MS },
  };
}

/**
 * @param fallbackPrimary - Label par défaut (ex: "Commencer l'évaluation")
 * @param fallbackSecondary - Sous-texte par défaut optionnel
 */
export function useAdaptiveCta(
  fallbackPrimary: string,
  fallbackSecondary?: string
): AdaptiveCtaResult {
  const [result, setResult] = useState<AdaptiveCtaResult>({
    primaryLabel: fallbackPrimary,
    secondaryLabel: fallbackSecondary,
    variant: undefined,
    tone: "neutral",
    isAdaptive: false,
  });
  const fetched = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const sessionId =
      typeof sessionStorage !== "undefined" ? sessionStorage.getItem(UX_SESSION_STORAGE_KEY) : null;

    const applyFallback = () => {
      if (!cancelled) {
        setResult({
          primaryLabel: fallbackPrimary,
          secondaryLabel: fallbackSecondary,
          variant: undefined,
          tone: "neutral",
          isAdaptive: false,
        });
      }
    };

    const run = async () => {
      const flags = await getUxFlags();
      if (!flags.adaptiveUxEnabled || !flags.uxCtaEnabled || !sessionId) {
        applyFallback();
        return;
      }

      const cached = getCached(sessionId);
      if (cached) {
        if (!cancelled) setResult(cached);
        return;
      }

      const ac = new AbortController();
      const timeoutId = setTimeout(() => ac.abort(), FETCH_TIMEOUT_MS);

      try {
        const r = await fetch(`/api/ux-cta?session_id=${encodeURIComponent(sessionId)}`, {
          credentials: "same-origin",
          signal: ac.signal,
        });
        clearTimeout(timeoutId);
        if (cancelled) return;
        if (r.status !== 200) {
          applyFallback();
          return;
        }
        const data = (await r.json()) as {
          variant: CtaVariant;
          primaryLabel: string;
          secondaryLabel?: string;
          tone: "soft" | "neutral" | "strong";
        };
        const next: AdaptiveCtaResult = {
          primaryLabel: data.primaryLabel,
          secondaryLabel: data.secondaryLabel,
          variant: data.variant,
          tone: data.tone,
          isAdaptive: true,
        };
        setCached(sessionId, next);
        setResult(next);
      } catch {
        if (!cancelled) applyFallback();
      }
    };

    if (!fetched.current) {
      fetched.current = true;
      void run();
    }

    return () => {
      cancelled = true;
    };
  }, [fallbackPrimary, fallbackSecondary]);

  return result;
}
